/**
 * Iter 44 — Direct download helpers.
 *
 * The Projects app taught us that `<a href download>` on cross-origin CDN URLs
 * ignores the `download` hint in every browser (the file just opens in a new
 * tab). The fix: fetch the URL as a Blob, materialize it under a same-origin
 * object URL, then trigger `<a download>` on that. This gives us:
 *   • True forced-download (no new tab)
 *   • Correct filename (title + size suffix + real extension)
 *   • A progress signal (the Promise resolves when the blob is fully in memory)
 *
 * Bunny CDN sends `Access-Control-Allow-Origin: *` on Pull-Zone reads, so
 * `fetch()` does not need credentials and won't be blocked by CORS.
 */

// Extension inferred from asset content_type. Falls back to the URL path's
// extension for the rare case where content_type is empty.
const MIME_TO_EXT = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/heic": "heic",
  "image/heif": "heif",
  "application/pdf": "pdf",
};

export function extForAsset(asset) {
  const ct = (asset?.content_type || "").toLowerCase().split(";")[0].trim();
  if (MIME_TO_EXT[ct]) return MIME_TO_EXT[ct];
  const path = (asset?.storage_path || asset?.cdn_url || asset?.original_filename || "").toLowerCase();
  const m = path.match(/\.([a-z0-9]{2,5})(?:\?|$)/);
  return m ? m[1] : "bin";
}

// Sanitize an asset title for use in a filename. Preserves case + hyphens,
// replaces spaces with underscores, strips characters that Windows/macOS
// filesystems reject, collapses repeats.
export function sanitizeTitle(title) {
  if (!title) return "asset";
  return String(title)
    .trim()
    .replace(/[\\/:*?"<>|]/g, "")   // filesystem-illegal characters
    .replace(/\s+/g, "_")             // spaces → underscores
    .replace(/_+/g, "_")              // collapse repeated underscores
    .replace(/^[_.-]+|[_.-]+$/g, "")  // trim leading/trailing punctuation
    || "asset";
}

// Map a size label from the /image-qualities response to a filename suffix.
// "original" → "Original", "75%" → "75pc", "50%" → "50pc", "25%" → "25pc".
export function suffixForResolution(resolution) {
  if (!resolution) return "";
  const r = String(resolution).toLowerCase();
  if (r === "original") return "Original";
  const pct = r.match(/^(\d{1,3})\s*%?$/);
  if (pct) return `${pct[1]}pc`;
  // Fallbacks like "1080p" — keep as-is.
  return String(resolution).replace(/\s+/g, "_");
}

// Assemble the final download filename.
// Examples:
//   { title: "BPS Club Still", resolution: "75%", ext: "jpg" }
//     → "BPS_Club_Still_75pc.jpg"
//   { title: "Client Brief", resolution: null, ext: "pdf" }
//     → "Client_Brief.pdf"
export function buildAssetFilename({ title, resolution, ext }) {
  const base = sanitizeTitle(title);
  const suffix = suffixForResolution(resolution);
  const safeExt = (ext || "bin").replace(/^\./, "");
  return suffix ? `${base}_${suffix}.${safeExt}` : `${base}.${safeExt}`;
}

// The core fetch-to-blob primitive. Throws on non-2xx so callers can toast
// the error.
export async function downloadBlob(url, filename) {
  const res = await fetch(url, { credentials: "omit" });
  if (!res.ok) {
    throw new Error(`Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Give the browser a beat to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
  }
}
